import { useEffect } from 'react';
import { ExternalLink, HelpCircle, Info, KeyRound, X } from 'lucide-react';
import { SocialPlatform } from '@mg/shared';
import { cn } from '../lib/cn';
import { LinkedInIcon } from '../features/social/brand-icons';
import { GuideByoaNote } from '../features/social/GuideByoaNote';
import { PLATFORM_META } from '../features/social/platforms';
import { useOAuthRedirectUris } from '../features/social/useOAuthRedirectUris';

/** A LinkedIn OAuth scope, as the guide names it. */
function scope(name: string) {
  return <code className="rounded bg-chip px-1 text-xs">{name}</code>;
}

/**
 * Standalone "Get your LinkedIn Client ID & Secret" guide, opened in a new tab
 * from Site Integrations. Walks a non-developer through creating a LinkedIn app,
 * adding the required products, registering the redirect URL, and copying the
 * Client ID + Client Secret. Renders OUTSIDE the agency nav shell and needs no auth.
 */
export function LinkedInAppGuide() {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Get your LinkedIn Client ID & Secret · Smorgas Social';
    return () => {
      document.title = prev;
    };
  }, []);
  // The OAuth redirect URL the install's LinkedIn apps must register, as the server sends it.
  const redirectUri = useOAuthRedirectUris()?.linkedin ?? null;

  const steps: { title: string; body: React.ReactNode }[] = [
    {
      title: 'Open the LinkedIn Developers site',
      body: (
        <>
          Go to{' '}
          <a
            href="https://www.linkedin.com/developers/apps"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-medium text-link hover:text-action-hover"
          >
            linkedin.com/developers/apps <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>{' '}
          and sign in with your LinkedIn account.
        </>
      ),
    },
    {
      title: 'Create a new app',
      body: (
        <>
          Click <span className="font-semibold">Create app</span>. Enter an app name (e.g.
          &ldquo;My Smorgas Social&rdquo;), and associate it with the{' '}
          <span className="font-semibold">LinkedIn Company Page you want to post to</span>. You must
          be a <span className="font-semibold">Super admin</span> of that Page to associate it.
          Upload a logo, accept the terms, and create the app.
        </>
      ),
    },
    {
      title: 'Verify your app',
      body: (
        <>
          On the <span className="font-semibold">Settings</span> tab, click{' '}
          <span className="font-semibold">Verify</span> next to your associated Page and complete the
          one-click verification (a Page admin approves a link). The{' '}
          <span className="font-semibold">Community Management API requires a verified app</span>, so
          don&apos;t skip this.
        </>
      ),
    },
    {
      title: 'Add the product that matches how you want to post',
      body: (
        <>
          Each connection type has its own row in Site Integrations and uses its own LinkedIn app.
          Open the <span className="font-semibold">Products</span> tab and request the one for the
          type you need (repeat these steps with a second app for the other type):
          <ul className="mt-1.5 list-disc space-y-1.5 pl-5">
            <li>
              <span className="font-semibold">Post to a Company Page</span> (most common) → request
              the <span className="font-semibold">Community Management API</span>. Its{' '}
              <span className="font-semibold">Development Tier</span> is enough to get started.
              LinkedIn reviews each request, which can take days or weeks. Its keys go in the{' '}
              <span className="font-semibold">LinkedIn · Community Management</span> row of
              Site Integrations.
            </li>
            <li>
              <span className="font-semibold">Post to your personal profile</span> → request{' '}
              <span className="font-semibold">Sign In with LinkedIn using OpenID Connect</span> +{' '}
              <span className="font-semibold">Share on LinkedIn</span>. Its keys go in the{' '}
              <span className="font-semibold">LinkedIn · Sign In + Share</span> row — a separate app
              from the Community Management one.
            </li>
          </ul>
        </>
      ),
    },
    {
      title: 'Register the redirect URL',
      body: (
        <>
          Open the <span className="font-semibold">Auth</span> tab and add this exact value under{' '}
          <span className="font-semibold">Authorized redirect URLs for your app</span>, then save
          (no trailing slash, must match character-for-character):
          <code
            className={cn(
              'mt-2 block break-all rounded-lg bg-chip px-3 py-2 text-xs',
              redirectUri ? 'text-ink' : 'text-ink-faint',
            )}
          >
            {redirectUri ?? 'Loading…'}
          </code>
        </>
      ),
    },
    {
      title: 'Copy your Client ID and Client Secret',
      body: (
        <>
          Still on the <span className="font-semibold">Auth</span> tab, copy the{' '}
          <span className="font-semibold">Client ID</span> and{' '}
          <span className="font-semibold">Client Secret</span>. Paste both into the matching
          LinkedIn row under Site Integrations → Social Media Integrations in Smorgas Social (a
          Tenant Admin does this).
        </>
      ),
    },
  ];

  return (
    // h-full + overflow-y-auto: these guides are STANDALONE routes (no AppShell), and
    // body/#root are locked to overflow:hidden so the app's inner regions own scrolling.
    // Without its own scroll region a guide taller than the viewport is simply clipped.
    <div className="h-full overflow-y-auto bg-page text-ink">
      <div className="mx-auto max-w-2xl px-5 py-10 sm:py-14">
        {/* Branding. The page follows the theme, so the logo does too: dark lettering on the light
            page, white on the dark one. The files have no size of their own, hence width and height,
            drawn in rem by the classes so the logo scales with the page (index.css). */}
        <div className="mb-8 flex items-center">
          <img
            src="/smorgas-social-logo-light.svg"
            alt="Smorgas Social"
            width={137}
            height={21}
            className="block h-[1.3125rem] w-[8.5625rem] dark:hidden"
          />
          <img
            src="/smorgas-social-logo.svg"
            alt="Smorgas Social"
            width={137}
            height={21}
            className="hidden h-[1.3125rem] w-[8.5625rem] dark:block"
          />
        </div>

        <div className="rounded-2xl border border-line bg-card p-6 shadow-card sm:p-8">
          <div className="mb-6 flex items-start gap-3">
            <span
              className={cn(
                'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl',
                PLATFORM_META[SocialPlatform.LINKEDIN].tile,
              )}
            >
              <LinkedInIcon className="h-6 w-6" />
            </span>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-ink">
                Get your LinkedIn Client ID &amp; Secret
              </h1>
              <p className="mt-1 text-sm text-ink-muted">
                Smorgas Social connects to LinkedIn through{' '}
                <span className="font-medium">LinkedIn apps</span> your organization owns — one for
                Company Pages (Community Management) and one for personal profiles
                (Sign In + Share). A Tenant Admin sets each up once and adds its two values in
                Site Integrations.
              </p>
            </div>
          </div>

          <ol className="space-y-5">
            {steps.map((step, i) => (
              <li key={i} className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-tint text-sm font-bold text-tint-fg">
                  {i + 1}
                </span>
                <div className="pt-0.5">
                  <p className="text-sm font-semibold text-ink">{step.title}</p>
                  <div className="mt-1 text-sm leading-relaxed text-ink-label">{step.body}</div>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-7 flex gap-3 rounded-xl border border-amber-100 dark:border-warning-dark/30 bg-amber-50/70 dark:bg-warning-dark-tint/70 px-4 py-3">
            <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-amber-500 dark:text-warning-dark" aria-hidden />
            <div className="text-sm text-amber-800/90 dark:text-warning-dark">
              <p className="font-semibold text-amber-900 dark:text-warning-dark">Keep your Client Secret private</p>
              <p className="mt-1">
                Treat the Client Secret like a password. Smorgas Social keeps it on its server,
                never shows it again and uses it only to complete LinkedIn sign-ins — don&apos;t
                share it elsewhere or post it publicly.
              </p>
            </div>
          </div>

          <div className="mt-4 flex gap-3 rounded-xl border border-line bg-tint/70 px-4 py-3">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-tint-fg" aria-hidden />
            <div className="text-sm text-ink-strong/90">
              <p className="font-semibold text-ink">Good to know</p>
              <ul className="mt-1 list-disc space-y-1 pl-4">
                <li>
                  The redirect URL must match <span className="font-semibold">exactly</span> — copy
                  it from this page or from Site Integrations. Both LinkedIn apps use this same URL.
                </li>
                <li>
                  Your app&apos;s <span className="font-semibold">Auth</span> tab must list every
                  scope Smorgas Social asks for. Asking for a scope your app doesn&apos;t have
                  triggers LinkedIn&apos;s generic{' '}
                  <span className="font-semibold">&ldquo;Bummer, something went wrong&rdquo;</span>{' '}
                  screen.
                  <ul className="mt-1 list-disc space-y-1 pl-4">
                    <li>
                      Community Management asks for {scope('rw_organization_admin')},{' '}
                      {scope('w_organization_social')} and {scope('r_organization_social_feed')}.
                    </li>
                    <li>
                      Sign In + Share asks for {scope('openid')}, {scope('profile')},{' '}
                      {scope('email')} and {scope('w_member_social')}.
                    </li>
                  </ul>
                </li>
                <li>
                  The Community Management <span className="font-semibold">Development Tier</span> is
                  limited-volume but fine to start; request the Standard Tier upgrade later for more
                  throughput.
                </li>
              </ul>
            </div>
          </div>

          <GuideByoaNote />

          <div className="mt-4 flex gap-3 rounded-xl border border-success/20 dark:border-success-dark/30 bg-success-tint/70 dark:bg-success-dark-tint/70 px-4 py-3">
            <HelpCircle className="mt-0.5 h-5 w-5 shrink-0 text-success dark:text-success-dark" aria-hidden />
            <div className="text-sm text-success/90 dark:text-success-dark">
              <p className="font-semibold text-success dark:text-success-dark">
                Seeing &ldquo;No Company Pages found&rdquo; after connecting?
              </p>
              <p className="mt-1">
                LinkedIn only lets you post to Pages where you hold a posting role. Check each of
                these:
              </p>
              <ul className="mt-1 list-disc space-y-1 pl-4">
                <li>
                  You must be a <span className="font-semibold">Super admin</span> or{' '}
                  <span className="font-semibold">Content admin</span> of the Page. Analyst, Curator,
                  and paid-media-only roles can&apos;t publish, so those Pages won&apos;t appear.
                  Check at your Page → <span className="font-semibold">Admin tools → Manage admins</span>.
                </li>
                <li>
                  If your admin access is still <span className="font-semibold">pending</span> (you
                  were invited but haven&apos;t accepted), accept the invitation on LinkedIn first —
                  pending Pages are hidden until then.
                </li>
                <li>
                  Sign in with the <span className="font-semibold">same LinkedIn account</span> that
                  admins the Page. Connected with the wrong account? Hit{' '}
                  <span className="font-semibold">Disconnect</span> in Smorgas Social and reconnect.
                </li>
                <li>
                  If you previously revoked this app (LinkedIn →{' '}
                  <span className="font-semibold">Settings → Data privacy → Permitted services</span>)
                  or its token expired, <span className="font-semibold">Disconnect</span> and
                  reconnect to mint a fresh token.
                </li>
              </ul>
            </div>
          </div>
        </div>

        <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-sm text-ink-faint">
          <X className="h-4 w-4" aria-hidden /> You can close this tab and return to Smorgas Social
          when you&apos;re done.
        </p>
      </div>
    </div>
  );
}
